import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Download } from "lucide-react";
import { motion } from "framer-motion";
import Layout from "@/components/layout/Layout";
import SEO from "@/components/SEO";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import heroImage from "@/assets/hero-marquee.jpg";
import sellSheetDetail from "@/assets/sell-sheet-detail.jpg";
import sellSheetInstall from "@/assets/sell-sheet-install.jpg";
import sellSheetLetters from "@/assets/sell-sheet-letters.jpg";
import styleModern from "@/assets/style-modern.jpg";
import styleAdm from "@/assets/style-adm.jpg";
import styleCondensed from "@/assets/style-condensed.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6 },
  }),
};

const letterStyles = [
  {
    title: "Modern",
    desc: "Clean, contemporary letterforms with excellent readability. Available in 3″ through 10″.",
    image: styleModern,
    slug: "modern-letter-set",
  },
  {
    title: "ADM",
    desc: "Bold, attention-grabbing letterforms ideal for high-visibility signage. Available in 4″ through 10″.",
    image: styleAdm,
    slug: "adm-letter-set",
  },
  {
    title: "Condensed",
    desc: "Narrow letterforms that fit more text per line — perfect when space is tight. Available in 4″ through 10″.",
    image: styleCondensed,
    slug: "condensed-letter-set",
  },
];

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Pronto Letters by Gemini",
  url: "https://pronto-letters.com",
  logo: "https://pronto-letters.com/favicon.png",
  description: "Gemini Inc. manufacturer of Pronto™ injection-molded changeable letter sets for signs.",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+1-314-369-8649",
    contactType: "sales",
  },
};

const webSiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Pronto Letters by Gemini",
  url: "https://pronto-letters.com",
  potentialAction: {
    "@type": "SearchAction",
    target: "https://pronto-letters.com/shop",
    "query-input": "required name=search_term_string",
  },
};

export default function Index() {
  return (
    <Layout>
      <SEO
        title="Gemini Pronto™ Changeable Letter Sets"
        description="Shop Gemini Pronto™ injection-molded changeable letter sets for signs. Modern, ADM, and Condensed styles in Black, Red & White — 3″ through 10″."
        canonical="/"
        jsonLd={[organizationSchema, webSiteSchema]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="container grid items-center gap-8 py-8 lg:grid-cols-[3fr_2fr] lg:py-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="order-1 flex justify-center"
          >
            <Dialog>
              <DialogTrigger asChild>
                <img
                  src={heroImage}
                  alt="Pronto changeable letters on a sign"
                  className="w-full max-w-[70%] cursor-zoom-in rounded-lg shadow-2xl transition-transform duration-300 hover:scale-105"
                />
              </DialogTrigger>
              <DialogContent className="max-w-4xl p-2">
                <img src={heroImage} alt="Pronto changeable letters on a sign" className="w-full" />
              </DialogContent>
            </Dialog>
          </motion.div>
          <div className="order-2 flex flex-col lg:items-start lg:pl-8">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl lg:text-4xl"
            >
              A better letter
              <br />
              <span className="text-accent">lasts longer.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-4 max-w-xs text-sm text-primary-foreground/70"
            >
              Gemini Pronto™ changeable letter sets — durable, vibrant, and effortless to install on any sign.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-6 flex flex-wrap gap-3"
            >
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
                <Link to="/shop">
                  Shop Letter Sets <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Three Letter Styles */}
      <section className="container py-12">
        <h2 className="text-center font-display text-3xl font-semibold">Choose Your Letter Style</h2>
        <p className="mx-auto mt-3 max-w-xl text-center text-muted-foreground">
          Whether you're ordering for the first time or matching existing letters, pick the style that fits your sign.
        </p>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {letterStyles.map((style, i) => (
            <motion.div
              key={style.title}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={fadeUp}
            >
              <Link
                to={`/shop/${style.slug}`}
                className="group block cursor-pointer overflow-hidden rounded-lg border bg-card transition-shadow hover:shadow-lg hover:ring-2 hover:ring-accent"
              >
                <div className="overflow-hidden bg-white p-4">
                  <img
                    src={style.image}
                    alt={`${style.title} letter style — sizes and color availability`}
                    className="w-full object-contain transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-bold">{style.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{style.desc}</p>
                  <span className="mt-4 inline-flex items-center text-sm font-medium text-accent">
                    View Details & Order <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Product info callout with sell sheet */}
      <section className="bg-primary text-primary-foreground">
        <div className="container grid items-center gap-12 py-20 md:grid-cols-2">
          <div>
            <h2 className="font-display text-3xl font-bold">Injection-Molded Quality</h2>
            <p className="mt-4 text-primary-foreground/70">
              Gemini Pronto™ letters are a cut above traditional saw-cut changeable copy. Each panel is molded with
              raised edges to prevent scratching, plus molded-in feet to keep debris and ice from interfering with changes.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-primary-foreground/80">
              <li>✓ Impact-modified injection-molded acrylic blanks</li>
              <li>✓ UV-cured water-based inks — zero VOCs</li>
              <li>✓ Raised edges protect lettering from scratches</li>
              <li>✓ Molded feet prevent freeze-in during cold weather</li>
              <li>✓ Available in Modern, ADM & Condensed styles</li>
              <li>✓ 3″ through 10″ letter heights</li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
                <Link to="/shop">View All Products</Link>
              </Button>
              <a
                href="/docs/Pronto-Sell-Sheet.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-primary-foreground/30 px-4 py-2.5 text-sm font-medium transition-colors hover:bg-primary-foreground/10"
              >
                <Download className="h-4 w-4" /> Download Sell Sheet
              </a>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="flex justify-center overflow-hidden rounded-lg shadow-2xl"
          >
            <img src={sellSheetLetters} alt="Pronto letter styles and sizes" className="w-1/2" />
          </motion.div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="bg-secondary py-20">
        <div className="container">
          <h2 className="text-center font-display text-3xl font-semibold">Shop by Category</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <Link
              to="/shop?category=letter-set"
              className="group relative flex h-64 items-end overflow-hidden rounded-lg border bg-card p-8 transition-shadow hover:shadow-lg"
            >
              <img
                src={sellSheetDetail}
                alt="Letter sets"
                className="absolute inset-0 h-full w-full object-cover opacity-20 transition-opacity group-hover:opacity-30"
              />
              <div className="relative">
                <h3 className="font-display text-2xl font-bold">Letter Sets</h3>
                <p className="mt-1 text-sm text-muted-foreground">Modern, ADM & Condensed styles</p>
                <span className="mt-3 inline-flex items-center text-sm font-medium text-accent">
                  Browse sets <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
            <Link
              to="/shop?category=accessory"
              className="group relative flex h-64 items-end overflow-hidden rounded-lg border bg-card p-8 transition-shadow hover:shadow-lg"
            >
              <img
                src={sellSheetInstall}
                alt="Accessories"
                className="absolute inset-0 h-full w-full object-cover opacity-20 transition-opacity group-hover:opacity-30"
              />
              <div className="relative">
                <h3 className="font-display text-2xl font-bold">Accessories</h3>
                <p className="mt-1 text-sm text-muted-foreground">Track, rivets, change arms & storage</p>
                <span className="mt-3 inline-flex items-center text-sm font-medium text-accent">
                  Browse accessories <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
